import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Users, Heart, MessageCircle, Phone, Video, Coffee } from 'lucide-react';

interface SocialConnectionTrackerProps {
  initialData?: {
    meaningful_interactions?: number;
    felt_connected?: boolean;
    felt_lonely?: boolean;
    social_activity_type?: string;
    social_notes?: string;
  };
  onDataChange: (data: any) => void;
}

export default function SocialConnectionTracker({ initialData = {}, onDataChange }: SocialConnectionTrackerProps) {
  const [interactions, setInteractions] = useState<number>(initialData.meaningful_interactions || 0);
  const [feltConnected, setFeltConnected] = useState<boolean | undefined>(initialData.felt_connected);
  const [feltLonely, setFeltLonely] = useState<boolean | undefined>(initialData.felt_lonely);
  const [activityType, setActivityType] = useState<string>(initialData.social_activity_type || '');
  const [notes, setNotes] = useState<string>(initialData.social_notes || '');

  const handleInteractionChange = (value: number) => {
    setInteractions(value);
    onDataChange({
      meaningful_interactions: value,
      felt_connected: feltConnected,
      felt_lonely: feltLonely,
      social_activity_type: activityType,
      social_notes: notes
    });
  };

  const handleConnectionChange = (value: string) => {
    const connected = value === 'yes';
    setFeltConnected(connected);
    onDataChange({
      meaningful_interactions: interactions,
      felt_connected: connected,
      felt_lonely: feltLonely,
      social_activity_type: activityType,
      social_notes: notes
    });
  };

  const handleLonelyChange = (value: string) => {
    const lonely = value === 'yes';
    setFeltLonely(lonely);
    onDataChange({
      meaningful_interactions: interactions,
      felt_connected: feltConnected,
      felt_lonely: lonely,
      social_activity_type: activityType,
      social_notes: notes
    });
  };

  const handleActivityTypeChange = (type: string) => {
    const newType = activityType === type ? '' : type;
    setActivityType(newType);
    onDataChange({
      meaningful_interactions: interactions,
      felt_connected: feltConnected,
      felt_lonely: feltLonely,
      social_activity_type: newType,
      social_notes: notes
    });
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    onDataChange({
      meaningful_interactions: interactions,
      felt_connected: feltConnected,
      felt_lonely: feltLonely,
      social_activity_type: activityType,
      social_notes: value
    });
  };

  const activityTypes = [
    { id: 'in-person', label: 'In-Person', icon: Users },
    { id: 'phone', label: 'Phone Call', icon: Phone },
    { id: 'video', label: 'Video Call', icon: Video },
    { id: 'message', label: 'Messaging', icon: MessageCircle },
    { id: 'group', label: 'Group Activity', icon: Coffee }
  ];

  return (
    <Card className="p-6 space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-bold">Social Connection</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Track your meaningful social interactions and connection quality
        </p>
      </div>

      {/* Meaningful Interactions */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Meaningful Interactions Today</Label>
        <p className="text-sm text-muted-foreground">
          Quality conversations where you felt heard and understood
        </p>
        <div className="flex gap-2 flex-wrap">
          {[0, 1, 2, 3, 4, 5].map((num) => (
            <Button
              key={num}
              type="button"
              variant={interactions === num ? "default" : "outline"}
              size="lg"
              onClick={() => handleInteractionChange(num)}
              className="w-16 h-16"
            >
              {num}
            </Button>
          ))}
        </div>
      </div>

      {/* Activity Types */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Type of Social Activity</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {activityTypes.map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              type="button"
              variant={activityType === id ? "default" : "outline"}
              onClick={() => handleActivityTypeChange(id)}
              className="h-auto py-4 flex flex-col items-center gap-2"
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm">{label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Felt Connected */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Did you feel connected to others?</Label>
        <RadioGroup 
          value={feltConnected === undefined ? '' : feltConnected ? 'yes' : 'no'}
          onValueChange={handleConnectionChange}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="connected-yes" />
            <Label htmlFor="connected-yes" className="font-normal cursor-pointer">
              Yes, I felt connected and supported
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="connected-no" />
            <Label htmlFor="connected-no" className="font-normal cursor-pointer">
              No, I felt disconnected
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Felt Lonely */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Did you experience loneliness?</Label>
        <RadioGroup 
          value={feltLonely === undefined ? '' : feltLonely ? 'yes' : 'no'}
          onValueChange={handleLonelyChange}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="lonely-yes" />
            <Label htmlFor="lonely-yes" className="font-normal cursor-pointer">
              Yes, I felt lonely or isolated
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="lonely-no" />
            <Label htmlFor="lonely-no" className="font-normal cursor-pointer">
              No, I felt socially satisfied
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Notes */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Reflection (Optional)</Label>
        <Textarea
          placeholder="How did your social interactions make you feel? Any insights about your connections?"
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          rows={3}
          className="resize-none"
        />
      </div>
    </Card>
  );
}
